import { formatDate, getEndOfDay, getStartOfDay } from 'commons/utils/date'
import { getUser } from 'commons/utils/user'
import { caracteristicasOptions, Icon, StatusType, Tooltip } from 'components'
import {
  type CustomBoolean,
  OriginOrder,
  StatusEDI,
  type StatusNdType,
  TypesValidations,
  YesNoNull,
} from 'constants/enums'
import type { DraftProps, Ordemvenda } from 'container/Home/components/Banners/interface'
import type { ClientsListInterface } from 'container/SalesOrder/components/ModalPostOn'
import dayjs from 'dayjs'
import _ from 'lodash'
import type { RequestOVsByFilters } from 'services/manage_ov/interface'
import type { SalesOrderData } from 'store/SalesOrderContext/types/SalesOrderData'
import { store } from 'store/store'
import { theme } from 'styles/theme/hav-theme'

import type { ViewByType } from '..'

export const convertArray = (value?: string) => {
  if (typeof value === 'string' && value !== '') {
    return value?.trim().split(',')
  }
  return []
}

export const convertArrayNumber = (value: string[]) => {
  if (value) {
    return value.map((item: string) => {
      return parseInt(item)
    })
  }
  return []
}

// TODO: Refactor this to use ENUMs
export const getOrderStatusCodes = (values?: string[]) => {
  if (values?.length) {
    return values.map((status) => {
      if (status === 'Em aberto') return 0
      if (status === 'Em análise') return 10
      if (status === 'Aprovado') return 20
      if (status === 'Restrito') return 90
      if (status === 'Concluído') return 100
    })
  }
  return []
}

export const parseTriStateString = (value: YesNoNull) => {
  if (value === YesNoNull.NULL) return null
  return value === YesNoNull.TRUE
}

export interface FormatBodyType {
  codigosCores?: string | string[]
  codigoSapCliente?: string
  codigoTipoOrdem?: string
  codigoZona?: string | string[]
  codigosCentros?: string[]
  codigosMateriais?: string | string[]
  codigosStatusEstoque?: string[]
  codigosStatusOrdemVenda?: string[] | number[]
  codigosStatusPedido?: string | string[]
  dataFinal?: string
  dataInicial?: string
  documentoFatura?: string
  escritorioVenda?: string | string[]
  equipeVenda?: string | string[]
  mercado?: string | string[]
  hierarquia?: boolean | string
  materialMTO?: YesNoNull
  caracteristicas?: string[]
  numeroNotaFiscal?: string
  numeroOrdemVenda?: string
  numeroOrdemVendaSAP?: string
  numeroPedidoCliente?: string
  numeroPedidoRascunho?: string
  page?: number
  searchBox?: string
  periodo?: string
  flagPedidoAntecipado?: CustomBoolean
  flagExibeEdi?: string
  flagExibeNd?: string
  flagExibeVolumeExtra?: string
  flagExibeHC?: string
  numeroPedidoEDI?: string
  flagDataFixaEntrega?: YesNoNull
  dataCriacaoPedido?: string | null
  origem?: OriginOrder | OriginOrder[]
}

export const formatRequestBodyByFilters = (
  body: FormatBodyType,
  skipDates?: boolean,
  viewBy?: ViewByType,
  isReport?: boolean,
): RequestOVsByFilters => {
  const dates = body.periodo ? body.periodo.split(' - ') : []

  const initialDate = dates.length && !skipDates ? getStartOfDay(dates[0], 'DD/MM/YYYY') : null
  const endDate =
    dates.length && !skipDates && dates[1] !== dayjs().format('L')
      ? getEndOfDay(dates[1], 'DD/MM/YYYY')
      : null

  const { comercialStructure: offices } = store.getState().management
  const selectedOffices =
    typeof body.escritorioVenda === 'string'
      ? convertArray(body.escritorioVenda)
      : body.escritorioVenda
  const allOfficesSelected = selectedOffices?.length === offices.length

  const allTeams = offices.flatMap((office) => office.Equipes.map((team) => team.Codigo))
  const selectedTeams =
    typeof body.equipeVenda === 'string' ? convertArray(body.equipeVenda) : body.equipeVenda
  const allTeamsSelected = selectedTeams?.length === allTeams.length

  const allZones = offices.flatMap((office) =>
    office.Equipes.flatMap((team) => team.Regioes.flatMap((zone) => zone.Codigo)),
  )

  // TODO: Review this logic, allZones is different from the zones selected.
  // NOTE: allZones is not filtered by selected hierarchy.
  const selectedZones =
    typeof body.codigoZona === 'string' ? convertArray(body.codigoZona) : body.codigoZona
  const allZonesSelected = selectedZones?.length === allZones.length

  const optionsStatusCodes = [0, 10, 20, 90, 100]
  const selectedStatusCodes =
    typeof body.codigosStatusPedido === 'string'
      ? getOrderStatusCodes(body.codigosStatusPedido.split(','))
      : getOrderStatusCodes(body.codigosStatusPedido)
  const allStatusSelected = selectedStatusCodes?.length === optionsStatusCodes.length

  const caracteristicas =
    typeof body.caracteristicas === 'string'
      ? (body?.caracteristicas as string)?.split(',')
      : body.caracteristicas || []

  const exibeHC = caracteristicas?.find(
    (item) => item === caracteristicasOptions[0].title, // NOTE: 'Gerado no Hava Conecta'
  )
    ? TypesValidations.YES
    : null
  const exibeEdi = caracteristicas?.find(
    (item) => item === caracteristicasOptions[1].title, // NOTE: 'Gerado por EDI'
  )
    ? TypesValidations.YES
    : null
  const exibeNd = caracteristicas?.find(
    (item) => item === caracteristicasOptions[2].title, // NOTE: 'Negociação diferenciada (ND)'
  )
    ? TypesValidations.YES
    : null
  const exibeVolumeExtra = caracteristicas?.find(
    (item) => item === caracteristicasOptions[3].title, // NOTE: 'Com volume extra'
  )
    ? TypesValidations.YES
    : null

  let formattedBody = {
    ...body,
    codigoSapCliente: convertArray(body.codigoSapCliente),
    codigosCentros:
      typeof body?.codigosCentros === 'string'
        ? convertArray(body.codigosCentros)
        : body?.codigosCentros,
    codigosCores:
      typeof body?.codigosCores === 'string' ? convertArray(body.codigosCores) : body?.codigosCores,
    codigosMateriais:
      typeof body?.codigosMateriais === 'string'
        ? convertArray(body.codigosMateriais)
        : body?.codigosMateriais,
    codigosStatusOrdemVenda:
      typeof body.codigosStatusOrdemVenda === 'object'
        ? body.codigosStatusOrdemVenda
        : body.codigosStatusOrdemVenda &&
          convertArrayNumber(convertArray(body.codigosStatusOrdemVenda)),
    codigosStatusPedido: !allStatusSelected ? selectedStatusCodes : [],
    codigoZona: !allZonesSelected ? selectedZones : [],
    // TODO: Refactor this
    dataCriacaoPedido:
      body.dataCriacaoPedido && !['null', 'Invalid Date'].includes(body.dataCriacaoPedido)
        ? formatDate(body.dataCriacaoPedido, 'YYYY-MM-DDTHH:mm:ss')
        : null,
    dataFinal: endDate,
    dataInicial: initialDate,
    documentoFatura: body?.documentoFatura ? convertArray(body?.documentoFatura) : [],
    equipeVenda: !allTeamsSelected ? selectedTeams : [],
    escritorioVenda: !allOfficesSelected ? selectedOffices : [],
    flagDataFixaEntrega: parseTriStateString(body.flagDataFixaEntrega as YesNoNull),
    flagExibeEdi: exibeEdi,
    flagExibeHC: exibeHC,
    flagExibeNd: exibeNd,
    flagExibeVolumeExtra: exibeVolumeExtra,
    flagPedidoAntecipado:
      viewBy === 'orderAnticipation' ? TypesValidations.YES : TypesValidations.NO,
    hierarquia: typeof body.hierarquia === 'string' ? body.hierarquia === 'true' : body.hierarquia,
    materialMTO: parseTriStateString(body.materialMTO as YesNoNull),
    numeroNotaFiscal: convertArrayNumber(convertArray(body.numeroNotaFiscal)),
    numeroOrdemVenda: convertArray(body.numeroOrdemVenda),
    numeroOrdemVendaSAP: convertArray(body.numeroOrdemVendaSAP),
    numeroPedidoCliente: convertArray(body.numeroPedidoCliente),
    numeroPedidoEDI: convertArray(body.numeroPedidoEDI),
    numeroPedidoRascunho: convertArray(body.numeroPedidoRascunho),
    origem: typeof body?.origem === 'string' ? convertArray(body.origem) : body?.origem,
    searchBox:
      body.searchBox && body.searchBox.replaceAll('.', '').replaceAll('/', '').replaceAll('-', ''),
  }

  delete formattedBody.periodo
  delete formattedBody.caracteristicas

  if (isReport) delete formattedBody.page

  if (viewBy === 'orderAnticipation') {
    formattedBody = {
      ...formattedBody,
      codigosCentros: [],
      codigosStatusOrdemVenda: [],
      documentoFatura: [],
      flagExibeEdi: null,
      flagExibeHC: null,
      flagExibeNd: null,
      flagExibeVolumeExtra: null,
      numeroNotaFiscal: [],
      numeroOrdemVenda: [],
      numeroOrdemVendaSAP: [],
    }
  }

  const clearedBody = _.omitBy(formattedBody, (v) => v === undefined)

  return {
    ...clearedBody,
  }
}

export type GetNegDif = {
  flag: Ordemvenda['fl_negc_difd']
  isJustified: Ordemvenda['dt_just_negc_difd']
  ovStatus: number
}

const getIsNegDif = (ovs: GetNegDif[]): undefined | StatusNdType => {
  let isNegDif: undefined | StatusNdType

  for (const ov of ovs) {
    if (ov.flag === TypesValidations.YES && !ov.isJustified) return 'pending'
    if (ov.flag === TypesValidations.YES && ov.isJustified) {
      isNegDif = 'justified'
    }
  }
  const ovsNegDif = ovs.filter((ov) => ov.flag === TypesValidations.YES)

  const ovsNegDifApproved = ovs.filter(
    (ov) => ov.flag === TypesValidations.YES && ov.isJustified && ov.ovStatus > 10 && ov,
  )

  return isNegDif === 'justified' && ovsNegDifApproved.length === ovsNegDif.length
    ? 'approved'
    : isNegDif
}

export const getNegDifRowIcon = (ovs: GetNegDif[] | undefined): React.ReactElement => {
  const statusNd = ovs?.length ? getIsNegDif(ovs) : undefined

  const isNegDif: Record<StatusNdType, { title: string; color: string }> = {
    approved: {
      color: theme.colors.alert.success.dark,
      title: 'Justificativa aprovada',
    },
    justified: {
      color: theme.colors.neutral.light,
      title: 'Justificado',
    },
    pending: {
      color: theme.colors.alert.danger.dark,
      title: 'Justificativa pendente',
    },
  }

  if (statusNd === undefined) return <></>

  return (
    <Tooltip title={isNegDif[statusNd].title}>
      <span>
        <Icon color={isNegDif[statusNd].color} icon="nd" />
      </span>
    </Tooltip>
  )
}

export const getHavaRowIcon = (origin: OriginOrder, isOV = false) => {
  if (origin === OriginOrder.HAVA) {
    const message = isOV
      ? 'Pedido via Hava Conecta gerado com sucesso'
      : 'OV derivada de pedido gerado via Hava Conecta'

    return (
      <Tooltip title={message}>
        <span>
          <Icon icon="hc" />
        </span>
      </Tooltip>
    )
  }

  return <></>
}

export const getEDIRowIcon = (
  origin?: OriginOrder,
  status?: StatusEDI | null,
  isOV = false,
  isInternationalEdi = false,
) => {
  if (origin === OriginOrder.EDI && !isInternationalEdi) {
    const statusEDI: Record<StatusEDI, { title: string; color: string }> = {
      [StatusEDI.Success]: {
        color: theme.colors.alert.success.dark,
        title: 'Pedido via EDI gerado com sucesso',
      },
      [StatusEDI.Pending]: {
        color: theme.colors.alert.warning.dark,
        title: 'Pedido via EDI com solicitação pendente de alteração',
      },
      [StatusEDI.Error]: {
        color: theme.colors.alert.danger.dark,
        title: 'Erro na geração do pedido via EDI',
      },
    }

    // NOTE: For OVs, status is always null
    status = isOV ? null : StatusEDI.Success // TODO: Replace this "StatusEDI.Success" with "status" when EDI status is available by API

    const titleOrder = status ? statusEDI[status].title : 'OV derivada de pedido gerado via EDI'

    const title = isOV ? 'OV derivada de pedido gerado via EDI' : titleOrder
    const color = status ? statusEDI[status].color : theme.colors.neutral.light

    return (
      <Tooltip title={title}>
        <span>
          <Icon color={color} icon="edi" />
        </span>
      </Tooltip>
    )
  }

  return <></>
}

export const getPostOnBody = (
  array: ClientsListInterface[],
  draftBody: SalesOrderData,
  orderNumber?: string,
) => {
  const clientes = array.map((record) => ({
    cd_clie: record.cd_clie,
    fl_dupl_matr: TypesValidations.YES,
    fl_dupl_text: record?.text ? TypesValidations.YES : TypesValidations.NO,
    fl_mto_prep: draftBody.fl_mto_prep,
    nr_pedi_clie: record.nr_pedi_clie ?? '',
  }))

  const body = {
    Clientes: clientes,
    cd_usua_alte: getUser(),
    nr_pedi_rasc_orig: orderNumber || '',
  }

  return body
}

export function getOrderStatus(draft: DraftProps) {
  if (draft.CodigoStatusPedido === 0) return StatusType.ABERTO
  if (draft.CodigoStatusPedido === 10) return StatusType.ANALISE
  if (draft.CodigoStatusPedido === 90) return StatusType.RESTRITO

  return StatusType.CONCLUIDO
}
